import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Linkedin, CheckCircle2, Send, MapPin } from 'lucide-react';
import { profileData, skills, aboutMe } from '../mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CampaignCard from '../components/CampaignCard';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { translations, profileTranslations, aboutTranslations } from '../translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const t = translations[language];
  const profile = profileTranslations[language];
  const about = aboutTranslations[language];
  
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profilePhoto, setProfilePhoto] = useState('/joana-profile.jpg');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch campaigns from API
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await fetch(`${API_URL}/api/campaigns/`);
        const data = await response.json();
        setCampaigns(data);
      } catch (error) {
        console.error('Error fetching campaigns:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchProfilePhoto = async () => {
      try {
        const res = await fetch(`${API_URL}/api/profile/`);
        const data = await res.json();
        if (data.profilePhoto) {
          const url = data.profilePhoto.startsWith('/api/')
            ? `${API_URL}${data.profilePhoto}`
            : data.profilePhoto;
          setProfilePhoto(url);
        }
      } catch (error) {
        console.error('Error fetching profile photo:', error);
      }
    };

    fetchCampaigns();
    fetchProfilePhoto();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      toast({
        title: language === 'en' ? "Message sent!" : "Message envoyé !",
        description: language === 'en' 
          ? "Thank you for reaching out. I'll get back to you soon."
          : "Merci de m'avoir contacté. Je vous répondrai bientôt.",
      });
      setFormData({ name: '', email: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-start gap-12">
            {/* Profile Image */}
            <div className="flex-shrink-0">
              <div className="w-48 h-48 rounded-2xl overflow-hidden border-4 border-teal-100 shadow-xl bg-slate-100">
                <img 
                  src={profilePhoto}
                  alt="Joana Pestana Ramos"
                  className="w-full h-full object-cover"
                  data-testid="hero-profile-photo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect fill='%2314B8A6' width='200' height='200'/%3E%3Ctext fill='white' font-size='80' font-family='Arial' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EJP%3C/text%3E%3C/svg%3E";
                  }}
                />
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-6">
                <span className="text-sm font-medium tracking-wide text-teal-600 uppercase">
                  {profileData.yearsExperience} {t.hero.experience}
                </span>
                <span className="text-slate-300">•</span>
                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm font-medium">{profileData.location}</span>
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-2 leading-tight">
                {profile.tagline}
              </h1>
              <p className="text-xl md:text-2xl text-slate-600 mb-6">
                {profile.subtitle}
              </p>
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                {profile.statement}
              </p>
              <div className="flex flex-wrap gap-4 items-center">
                <a href="#portfolio" className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-all duration-300 hover:shadow-lg">
                  {t.hero.viewPortfolio}
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.linkedin.com/in/joanapr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-slate-700 px-6 py-3 rounded-lg border-2 border-slate-200 hover:border-teal-600 hover:text-teal-600 transition-all duration-300"
                >
                  <Linkedin className="w-5 h-5" />
                  {t.hero.connectLinkedIn}
                </a>
                <a href="mailto:joanapestana.ramos@gmail.com" className="inline-flex items-center gap-2 text-slate-700 px-6 py-3 rounded-lg border-2 border-slate-200 hover:border-teal-600 hover:text-teal-600 transition-all duration-300">
                  <Mail className="w-5 h-5" />
                  {t.hero.getInTouch}
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative element */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full blur-3xl opacity-30 -z-10"></div>
      </section>

      {/* Skills Section */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">{t.skills.title}</h2>
          <div className="space-y-6">
            {Object.entries(skills).map(([key, category]) => (
              <div key={key} className="bg-white p-6 rounded-xl border-2 border-slate-100">
                <h3 className="text-lg font-bold text-teal-600 mb-3">{t.skills[key].title}</h3>
                <div className="flex flex-wrap gap-2">
                  {t.skills[key].items.map((skill, index) => (
                    <span 
                      key={index}
                      className="text-slate-700 font-medium"
                    >
                      {skill}{index < t.skills[key].items.length - 1 ? ' •' : ''}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Languages Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">{t.languages.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.languages.items.map((language, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-xl border-2 border-slate-100 hover:border-teal-400 transition-all duration-300 text-center"
              >
                <div className="text-lg font-bold text-slate-900 mb-2">{language.name}</div>
                <div className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                  {language.level}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Me Section */}
      <section id="about" className="py-20 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            {about.title}
          </h2>
          <div className="space-y-6">
            {about.paragraphs.map((paragraph, index) => (
              <p key={index} className="text-lg text-slate-600 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <a 
              href={profileData.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-all duration-300"
            >
              <Linkedin className="w-5 h-5" />
              {t.hero.connectLinkedIn}
            </a>
            <a 
              href="#contact"
              className="inline-flex items-center gap-2 text-teal-600 px-6 py-3 rounded-lg border-2 border-teal-600 hover:bg-teal-50 transition-all duration-300"
            >
              <Mail className="w-5 h-5" />
              {t.hero.getInTouch}
            </a>
          </div>
        </div>
      </section>

      {/* Campaigns Section */}
      <section id="portfolio" className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">{t.portfolio.title}</h2>
            <p className="text-lg text-slate-600">{t.portfolio.subtitle}</p>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              <p className="mt-4 text-slate-600">Loading campaigns...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {campaigns.map((campaign) => (
                <CampaignCard key={campaign.id} campaign={campaign} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">{t.contact.title}</h2>
            <p className="text-lg text-slate-600">
              {t.contact.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">{t.hero.getInTouch}</h3>
              <div className="space-y-4">
              <a href={`mailto:${profileData.email}`} className="flex items-center gap-3 text-slate-700 hover:text-teal-600 transition-colors group">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center group-hover:bg-teal-600 transition-colors">
                    <Mail className="w-6 h-6 text-teal-600 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">{t.contact.email}</div>
                    <div className="font-medium">{profileData.email}</div>
                  </div>
                </a>
                <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-700 hover:text-teal-600 transition-colors group">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center group-hover:bg-teal-600 transition-colors">
                    <Linkedin className="w-6 h-6 text-teal-600 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">LinkedIn</div>
                    <div className="font-medium">{t.contact.connectWithMe}</div>
                  </div>
                </a>
              </div>

              <div className="mt-8 p-6 bg-teal-50 rounded-xl border-2 border-teal-100">
                <h4 className="font-bold text-slate-900 mb-2">{t.contact.openTo}</h4>
                <ul className="space-y-2 text-slate-700">
                  {t.contact.opportunities.map((opportunity, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span>{opportunity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    {t.contact.form.name}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-teal-600 focus:outline-none transition-colors"
                    placeholder={t.contact.form.namePlaceholder}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    {t.contact.form.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-teal-600 focus:outline-none transition-colors"
                    placeholder={t.contact.form.emailPlaceholder}
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    {t.contact.form.message}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-teal-600 focus:outline-none transition-colors resize-none"
                    placeholder={t.contact.form.messagePlaceholder}
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-all duration-300 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>{t.contact.form.sending}</>
                  ) : (
                    <>
                      {t.contact.form.send}
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;